import { useState, useEffect, useCallback } from 'react';
import appConfig from '../config';
import { ProductTilesResponse } from '../types/productTiles';

interface UseProductTilesReturn {
  tiles: ProductTilesResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useProductTiles = (): UseProductTilesReturn => {
  const [tiles, setTiles] = useState<ProductTilesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTiles = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const url = forceRefresh 
        ? `${appConfig.apiUrl}/product-tiles?force_refresh=true`
        : `${appConfig.apiUrl}/product-tiles`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Product tiles feature is not enabled');
        }
        throw new Error(`Failed to fetch product tiles: ${response.statusText}`);
      }
      
      const data: ProductTilesResponse = await response.json();
      setTiles(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch product tiles:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setTiles(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiles();
  }, [fetchTiles]);

  const refresh = useCallback(async () => {
    await fetchTiles(true);
  }, [fetchTiles]);

  return { tiles, loading, error, refresh };
};
